import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { businessAPI, reviewAPI } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import ConfirmModal from '../../ConfirmModal';

// 데모 계정 username
const DEMO_USERNAME = 'demo';

const ReviewTab = ({ businessId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // 데모 계정 여부
  const isDemo = user?.username === DEMO_USERNAME;
  
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('latest');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // 리뷰 작성/수정 상태
  const [writingMode, setWritingMode] = useState(null); // null | 'create' | 'edit' | 'reply'
  const [editingReview, setEditingReview] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [formData, setFormData] = useState({ stars: 5, text: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // 답글 관련 상태
  const [expandedReplies, setExpandedReplies] = useState(new Set());
  const [repliesData, setRepliesData] = useState({}); // reviewId -> replies array
  
  // Kebab 메뉴 상태
  const [openMenu, setOpenMenu] = useState(null);
  
  // openMenu 변경 감지
  useEffect(() => {
    const bottomSheet = document.querySelector('[data-rsbs-scroll]');
    console.log('openMenu changed to:', openMenu);
    console.log('Bottom sheet scroll after openMenu change:', bottomSheet?.scrollTop || 0);
  }, [openMenu]);
  
  // 삭제 확인 모달 상태
  const [deleteConfirmReviewId, setDeleteConfirmReviewId] = useState(null);

  useEffect(() => {
    loadReviews();
  }, [businessId, sortBy]);

  // 스크롤 위치 ref로 관리
  const scrollPosRef = useRef(0);

  useEffect(() => {
    const bottomSheet = document.querySelector('[data-rsbs-scroll]');
    console.log('WritingMode changed:', writingMode);
    console.log('Bottom sheet scroll:', bottomSheet?.scrollTop || 0);
    
    // writingMode가 변경될 때 스크롤 위치 복원
    if (writingMode && bottomSheet && scrollPosRef.current > 0) {
      requestAnimationFrame(() => {
        bottomSheet.scrollTop = scrollPosRef.current;
        console.log('WritingMode - Scroll restored to:', scrollPosRef.current);
      });
    }
    
    // 하단 작성칸이 확장될 때 스크롤 변경 감지
    if (writingMode) {
      setTimeout(() => {
        console.log('Bottom sheet scroll after writingMode expanded:', bottomSheet?.scrollTop || 0);
      }, 100);
    }
  }, [writingMode]);

  const loadReviews = async (loadMore = false) => {
    try {
      setLoading(true);
      const currentPage = loadMore ? page + 1 : 1;
      const response = await businessAPI.getReviews(businessId, {
        sort: sortBy,
        skip: (currentPage - 1) * 10,
        limit: 10
      });
      
      const reviewsData = Array.isArray(response.data) ? response.data : (response.data.reviews || []);
      
      if (loadMore) {
        setReviews([...reviews, ...reviewsData]);
        setPage(currentPage);
      } else {
        setReviews(reviewsData);
        setPage(1);
      }
      
      setHasMore(reviewsData.length === 10);
    } catch (error) {
      console.error('리뷰 로드 실패:', error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  // 답글 토글
  const toggleReplies = async (reviewId) => {
    const newExpanded = new Set(expandedReplies);
    
    if (newExpanded.has(reviewId)) {
      newExpanded.delete(reviewId);
    } else {
      newExpanded.add(reviewId);
      
      // 답글 로드 (아직 로드하지 않은 경우)
      if (!repliesData[reviewId]) {
        try {
          const response = await reviewAPI.getReplies(reviewId);
          setRepliesData({ ...repliesData, [reviewId]: response.data });
        } catch (error) {
          console.error('답글 로드 실패:', error);
          setRepliesData({ ...repliesData, [reviewId]: [] });
        }
      }
    }
    
    setExpandedReplies(newExpanded);
  };

  // 리뷰/답글 작성
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.text.trim()) {
      toast.dismiss();
      toast.error('내용을 입력해주세요.');
      return;
    }
    
    try {
      setSubmitting(true);
      
      if (writingMode === 'create') {
        // 새 리뷰 작성
        await businessAPI.createReview(businessId, {
          stars: formData.stars,
          text: formData.text
        });
        toast.dismiss();
        toast.success('리뷰가 작성되었습니다!');
      } else if (writingMode === 'edit') {
        // 리뷰 수정
        await reviewAPI.update(editingReview.id, {
          stars: formData.stars,
          text: formData.text
        });
        toast.dismiss();
        toast.success('리뷰가 수정되었습니다!');
      } else if (writingMode === 'reply') {
        // 답글 작성
        await reviewAPI.createReply(replyingTo, {
          text: formData.text
        });
        toast.dismiss();
        toast.success('답글이 작성되었습니다!');
      }
      
      // 초기화
      setFormData({ stars: 5, text: '' });
      setWritingMode(null);
      setEditingReview(null);
      setReplyingTo(null);
      
      // 리뷰 목록 새로고침
      loadReviews();
    } catch (error) {
      console.error('작성/수정 실패:', error);
      toast.dismiss();
      toast.error(error.response?.data?.detail || '작업에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  // 리뷰 삭제 확인 모달 표시
  const handleDelete = (reviewId) => {
    setDeleteConfirmReviewId(reviewId);
    setOpenMenu(null);
  };

  // 리뷰 삭제 실행
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmReviewId) return;
    
    try {
      await reviewAPI.delete(deleteConfirmReviewId);
      toast.dismiss();
      toast.success('삭제되었습니다.');
      setDeleteConfirmReviewId(null);
      loadReviews();
    } catch (error) {
      console.error('삭제 실패:', error);
      toast.dismiss();
      toast.error(error.response?.data?.detail || '삭제에 실패했습니다.');
      setDeleteConfirmReviewId(null);
    }
  };

  // 수정 시작
  const handleEditStart = (review) => {
    const bottomSheet = document.querySelector('[data-rsbs-scroll]');
    const scrollPos = bottomSheet?.scrollTop || 0;
    scrollPosRef.current = scrollPos; // ref에 저장
    console.log('handleEditStart called, scroll before:', scrollPos);
    
    setEditingReview(review);
    setFormData({ stars: review.stars || 5, text: review.text });
    setOpenMenu(null);
    setWritingMode('edit');
  };

  // 답글 시작
  const handleReplyStart = (reviewId) => {
    const bottomSheet = document.querySelector('[data-rsbs-scroll]');
    const scrollPos = bottomSheet?.scrollTop || 0;
    scrollPosRef.current = scrollPos; // ref에 저장
    console.log('handleReplyStart called, scroll before:', scrollPos);
    
    setReplyingTo(reviewId);
    setFormData({ stars: 5, text: '' });
    setOpenMenu(null);
    
    // writingMode가 이미 'reply'인 경우에도 스크롤 복원을 위해 일시적으로 null로 변경
    if (writingMode === 'reply') {
      setWritingMode(null);
      setTimeout(() => {
        setWritingMode('reply');
      }, 0);
    } else {
      setWritingMode('reply');
    }
  };

  const handleUserClick = (userId) => {
    if (userId) {
      navigate(`/profile/${userId}`);
    }
  };

  // 메뉴 닫기 (외부 클릭)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.kebab-menu')) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Kebab 메뉴 컴포넌트
  const KebabMenu = ({ review }) => {
    const isOwner = user && user.id === review.user_id;
    const isOpen = openMenu === review.id;
    
    return (
      <div className="kebab-menu">
        <button 
          type="button"
          className="kebab-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // 바텀시트 스크롤 위치 저장
            const bottomSheet = document.querySelector('[data-rsbs-scroll]');
            const scrollPos = bottomSheet?.scrollTop || 0;
            console.log('Kebab clicked - Bottom sheet scroll before:', scrollPos);
            console.log('Current openMenu:', openMenu, 'Review ID:', review.id);
            
            setOpenMenu(isOpen ? null : review.id);
            
            // 스크롤 위치 복원
            requestAnimationFrame(() => {
              if (bottomSheet && scrollPos > 0) {
                bottomSheet.scrollTop = scrollPos;
                console.log('Scroll restored to:', scrollPos);
              }
            });
          }}
        >
          ⋮
        </button>
        {isOpen && (
          <div className="kebab-dropdown">
            {isOwner ? (
              <>
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Edit clicked');
                    handleEditStart(review);
                  }}
                >
                  ✏️ 수정
                </button>
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Delete clicked');
                    handleDelete(review.id);
                  }} 
                  className="danger"
                >
                  🗑️ 삭제
                </button>
              </>
            ) : (
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Reply clicked');
                  handleReplyStart(review.id);
                }}
              >
                💬 답글 달기
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Useful 클릭 핸들러
  const handleUsefulClick = async (reviewId) => {
    try {
      await reviewAPI.incrementUseful(reviewId);
      // 로컬 상태 업데이트
      setReviews(reviews.map(r => 
        r.id === reviewId ? { ...r, useful: (r.useful || 0) + 1 } : r
      ));
      // 답글도 업데이트
      const newRepliesData = { ...repliesData };
      Object.keys(newRepliesData).forEach(parentId => {
        newRepliesData[parentId] = newRepliesData[parentId].map(r =>
          r.id === reviewId ? { ...r, useful: (r.useful || 0) + 1 } : r
        );
      });
      setRepliesData(newRepliesData);
    } catch (error) {
      console.error('Useful 증가 실패:', error);
    }
  };

  // 자동 확장 함수
  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  // 리뷰 아이템 컴포넌트
  const ReviewItem = ({ review, isReply = false }) => (
    <div 
      className={`review-item ${isReply ? 'reply-item' : ''} ${replyingTo === review.id ? 'replying-target' : ''}`}
    >
      {/* 미니멀 헤더: 이름 + 별점 (왼쪽) + 케밥 (오른쪽) */}
      <div className="review-minimal-header">
        <div className="review-minimal-header-left">
          <span 
            className="review-minimal-author"
            onClick={() => handleUserClick(review.user_id)}
          >
            {review.username || '익명'}
          </span>
          {!isReply && review.stars && (
            <span className="review-stars">
              {'⭐'.repeat(Math.floor(review.stars))}
            </span>
          )}
        </div>
        {/* Kebab 메뉴 (로그인한 경우만) */}
        {user && <KebabMenu review={review} />}
      </div>
      
      {/* 날짜 + useful 메타 정보 */}
      <div className="review-minimal-meta">
        <span>{new Date(review.created_at || review.date).toLocaleDateString()}</span>
      </div>
      
      {/* 리뷰 텍스트 */}
      <p className="review-text">{review.text}</p>
      
      {/* useful 버튼 */}
      <div className="review-minimal-footer">
        <button 
          type="button"
          className="useful-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleUsefulClick(review.id);
          }}
        >
          👍 {review.useful || 0}명이 도움됨
        </button>
      </div>
      
      {/* 답글 토글 버튼 (최상위 리뷰만, 답글이 있는 경우) */}
      {!isReply && review.reply_count > 0 && (
        <button 
          type="button"
          className="toggle-replies-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleReplies(review.id);
          }}
        >
          {expandedReplies.has(review.id) ? '▼' : '▶'} 답글 {review.reply_count}개
        </button>
      )}
      
      {/* 답글 목록 */}
      {!isReply && expandedReplies.has(review.id) && repliesData[review.id] && (
        <div className="replies-list">
          {repliesData[review.id].map(reply => (
            <ReviewItem key={reply.id} review={reply} isReply={true} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="review-tab">
      {/* 상단 헤더 */}
      <div className="review-header-top">
        <select 
          className="review-sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="latest">최신순</option>
          <option value="useful">추천순</option>
          <option value="stars_desc">별점 높은 순</option>
          <option value="stars_asc">별점 낮은 순</option>
        </select>
      </div>

      {/* 리뷰 리스트 */}
      {loading && reviews.length === 0 ? (
        <div className="loading">로딩 중...</div>
      ) : reviews.length === 0 ? (
        <div className="no-reviews">아직 리뷰가 없습니다</div>
      ) : (
        <>
          <div className="reviews-list">
            {reviews.map((review) => (
              <ReviewItem key={review.id || review.review_id} review={review} />
            ))}
          </div>

          {hasMore && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <button 
                type="button"
                className="review-load-more-link"
                onClick={() => loadReviews(true)}
                disabled={loading}
              >
                {loading ? '로딩 중...' : '더 보기'}
              </button>
            </div>
          )}
        </>
      )}
      
      {/* 하단 고정 작성칸 (position: fixed) - 데모 계정은 숨김 */}
      {user && !isDemo && (
        <div 
          className={`bottom-write-bar-fixed ${writingMode ? 'expanded' : ''}`}
          onClick={() => {
            if (!writingMode) {
              setWritingMode('create');
              setFormData({ stars: 5, text: '' });
            }
          }}
        >
          {!writingMode ? (
            <div className="bottom-write-placeholder">
              <span className="placeholder-text">리뷰를 작성해주세요...</span>
              <span className="placeholder-icon">✍️</span>
            </div>
          ) : (
            <form className="bottom-write-form" onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
              {/* 상단 바: 별점 + 닫기 버튼 */}
              <div className={`write-form-header ${writingMode === 'reply' ? 'reply-mode' : ''}`}>
                {/* 별점 (답글 작성 시는 표시 안함) */}
                {writingMode !== 'reply' && (
                  <div className="star-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-btn ${star <= formData.stars ? 'active' : ''}`}
                        onClick={() => setFormData({ ...formData, stars: star })}
                      >
                        ⭐
                      </button>
                    ))}
                  </div>
                )}
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => {
                    setWritingMode(null);
                    setEditingReview(null);
                    setReplyingTo(null);
                    setFormData({ stars: 5, text: '' });
                  }}
                >
                  ✕
                </button>
              </div>
              
              {/* 메인 영역: 텍스트 + 제출 버튼 */}
              <div className="form-content">
                <textarea
                  className="review-textarea"
                  placeholder={writingMode === 'reply' ? '답글을 입력해주세요...' : '리뷰를 작성해주세요...'}
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  onInput={(e) => autoResizeTextarea(e.target)}
                  required
                />
                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="icon-btn primary"
                    title={submitting ? '작성 중...' : 
                           writingMode === 'edit' ? '수정 완료' : 
                           writingMode === 'reply' ? '답글 등록' : '등록'}
                    disabled={submitting || !formData.text.trim()}
                  >
                    ✓
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      )}
      
      {(!user || isDemo) && (
        <div className="login-required-message-fixed">
          {isDemo ? '리뷰 작성은 로그인 후 이용할 수 있습니다.' : '리뷰를 작성하려면 로그인이 필요합니다.'}
        </div>
      )}

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteConfirmReviewId !== null}
        title="정말 삭제하시겠습니까?"
        message="답글도 함께 삭제됩니다."
        confirmText="삭제"
        cancelText="취소"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmReviewId(null)}
      />
    </div>
  );
};

export default ReviewTab;
